import { LitElement, html } from 'lit';
import { customElement, state } from 'lit/decorators.js';
import { effect } from '@preact/signals-core';
import { itemCount } from '../store/cart-store';

/**
 * Cart count badge for the header — red circle with item count.
 * Subscribes to cart store and updates reactively (no page refresh needed).
 */
@customElement('cart-count-badge')
export class CartCountBadge extends LitElement {
  @state() private _count = 0;

  private _disposeEffect?: () => void;

  connectedCallback() {
    super.connectedCallback();
    // Subscribe here (not in the constructor) to match the pattern used by
    // cart-count / cart-total: a constructor-time effect() writes reactive state
    // during construction, before Lit has connected/rendered.
    // NOTE: this did NOT resolve the lit-html "Cannot set properties of null
    // (setting 'data')" error that re-renders of this badge still throw on
    // cart update — that bug is still open (reproducible via requestUpdate()).
    this._disposeEffect = effect(() => {
      this._count = itemCount.value;
    });
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this._disposeEffect?.();
  }

  protected createRenderRoot(): HTMLElement | DocumentFragment {
    return this;
  }

  render() {
    const displayCount = this._count >= 100 ? '99+' : String(this._count);
    const hiddenClass = this._count === 0 ? ' hidden' : '';

    return html`
      <div class="cart-count-bubble absolute top-[-4px] right-[-4px] bg-[#e22020] h-[22px] w-[22px] rounded-full flex items-center justify-center text-white text-xs${hiddenClass}">
        <span id="item-count" aria-hidden="true">${displayCount}</span>
      </div>
    `;
  }
}

declare global {
  interface HTMLElementTagNameMap {
    'cart-count-badge': CartCountBadge;
  }
}
